import { useState } from "react";
import { UserPlus, UserMinus, Lock, Unlock, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { playerById } from "@/lib/playEngine";
import { BALL_STYLES } from "@/lib/playStyles";

export function RemoveDialog({ play, pending, onClose, onRemove }) {
  const others = play.players.filter((p) => p.id !== pending);
  const [to, setTo] = useState(others[0]?.id || "");
  if (!pending) return null;
  const p = playerById(play, pending);
  const evs = play.ball.events.filter((e) => e.from_id === pending || e.to_id === pending);
  const carrier = play.ball.carrier === pending;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111D35] border-[#1F3358] text-white" data-testid="remove-player-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">{p?.label} has the ball in this play</DialogTitle>
          <DialogDescription className="text-slate-300">
            {carrier && "They start with the ball. "}{evs.length > 0 && `They're part of ${evs.length} ball action${evs.length > 1 ? "s" : ""}: ${evs.map((e) => BALL_STYLES[e.type].label).join(", ")}.`} Choose what to do so the play stays correct.
          </DialogDescription>
        </DialogHeader>
        <label className="label" htmlFor="reassign">Give their ball actions to</label>
        <select id="reassign" className="input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="remove-reassign-select">
          {others.map((o) => <option key={o.id} value={o.id}>{o.label} ({o.side})</option>)}
        </select>
        <div className="flex flex-wrap gap-2 mt-2">
          <button className="btn btn-primary" disabled={!to} onClick={() => onRemove(pending, to)} data-testid="remove-reassign-btn">Reassign & remove player</button>
          <button className="btn btn-danger" onClick={() => onRemove(pending, null)} data-testid="remove-drop-actions-btn">Remove player & those actions</button>
          <button className="btn btn-ghost" onClick={onClose} data-testid="remove-cancel-btn">Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PlayerControls({ play, team, sel, locks, setLocks, onAdd, onRequestRemove }) {
  const selP = sel && playerById(play, sel);
  const count = (side) => play.players.filter((p) => p.side === side).length;
  const over = ["offense", "defense"].filter((s) => count(s) > team.player_count);
  const request = () => onRequestRemove(sel);
  const LockBtn = ({ side, label }) => (
    <button className={`btn px-3 ${locks[side] ? "btn-active" : "btn-ghost"}`} onClick={() => setLocks({ ...locks, [side]: !locks[side] })} aria-pressed={locks[side]} data-testid={`btn-lock-${side}`}>
      {locks[side] ? <Lock size={16} /> : <Unlock size={16} />}{locks[side] ? `${label} locked` : `Lock ${label}`}
    </button>
  );
  return (
    <div className="space-y-2" data-testid="player-controls">
      <div className="flex flex-wrap gap-1.5">
        <button className="btn btn-ghost px-3" onClick={() => onAdd("offense")} disabled={locks.offense || count("offense") >= 15} data-testid="btn-add-offensive-player"><UserPlus size={16} /><span className="w-3 h-3 rounded-full border-2 border-current" aria-hidden="true" />Add Offensive Player</button>
        <button className="btn btn-ghost px-3" onClick={() => onAdd("defense")} disabled={locks.defense || count("defense") >= 15} data-testid="btn-add-defensive-player"><UserPlus size={16} /><span className="w-2.5 h-2.5 rotate-45 border-2 border-current" aria-hidden="true" />Add Defensive Player</button>
        <button className="btn btn-danger px-3" onClick={request} disabled={!selP || locks[selP.side]} data-testid="btn-remove-selected-player"><UserMinus size={16} />Remove Selected{selP ? ` (${selP.label})` : " Player"}</button>
        <LockBtn side="offense" label="Offense" />
        <LockBtn side="defense" label="Defense" />
      </div>
      <p className="text-xs text-slate-400" data-testid="player-counts">Offense {count("offense")} · Defense {count("defense")} · Your format: {team.player_count} per side</p>
      {over.length > 0 && (
        <p className="text-xs text-[#FACC15] flex items-center gap-1" data-testid="player-count-warning"><AlertTriangle size={14} />{over.join(" and ")} has more than your usual {team.player_count} players. That's fine for teaching.</p>
      )}
    </div>
  );
}
